// Shared helpers for the Email workspace (EmailOverview.js, EmailThread.js,
// EmailComposer.js, EmailCustomerPanel.js, EmailMailbox.js).

// Must match EMAIL_CATEGORIES / EMAIL_PRIORITIES in server/email/store.js.
export const EMAIL_CATEGORIES = [
  { value: 'order_status', label: 'Order status' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'payment', label: 'Payment' },
  { value: 'return', label: 'Return' },
  { value: 'replacement', label: 'Replacement' },
  { value: 'cancellation', label: 'Cancellation' },
  { value: 'product_query', label: 'Product query' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'general', label: 'General' },
  { value: 'other', label: 'Other' },
];

export const EMAIL_PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

// Derived on the server from who wrote last (see STATUS_SQL in
// server/email/store.js).
export const EMAIL_STATUS = {
  needs_reply: { label: 'Needs reply', className: 'needs-reply' },
  waiting_customer: { label: 'Waiting on customer', short: 'Waiting', className: 'waiting' },
  resolved: { label: 'Resolved', className: 'resolved' },
};

export const categoryLabel = (value) => EMAIL_CATEGORIES.find((item) => item.value === value)?.label || '';
export const priorityLabel = (value) => EMAIL_PRIORITIES.find((item) => item.value === value)?.label || 'Normal';

export const EMAIL_ADDRESS_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const isEmailKey = (key) => String(key || '').includes('@');

const parseDate = (value) => {
  if (!value) return null;
  const parsed = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Conversation list: today = time, this year = "5 Oct", older = "5 Oct 24".
export const listTimestamp = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  const now = new Date();
  if (date.toDateString() === now.toDateString()) {
    return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    ...(date.getFullYear() === now.getFullYear() ? {} : { year: '2-digit' }),
  });
};

// "just now", "5 min ago", "3 h ago", "2 days ago", else a date.
export const relativeTime = (value, now = Date.now()) => {
  const date = parseDate(value);
  if (!date) return '';
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  if (seconds < 7 * 86400) {
    const days = Math.floor(seconds / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  return listTimestamp(value);
};

export const fullTimestamp = (value) => {
  const date = parseDate(value);
  if (!date) return '';
  return date.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// Midnight today in this browser's time zone, for the "Today" filter.
export const startOfTodayIso = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  return date.toISOString();
};

export const replySubject = (subject) => {
  const trimmed = String(subject || '').trim();
  if (!trimmed) return '';
  return /^re:/i.test(trimmed) ? trimmed : `Re: ${trimmed}`;
};

export const forwardSubject = (subject) => {
  const trimmed = String(subject || '').trim();
  if (!trimmed) return 'Fwd:';
  return /^fwd?:/i.test(trimmed) ? trimmed : `Fwd: ${trimmed}`;
};

const QUOTE_START = /^(On .+wrote:|-{2,}\s*Original Message\s*-{2,})\s*$/i;

// Splits a plain-text body into what was written this time and the quoted
// history below it ("On … wrote:" or a block of "> " lines at the end).
export const splitQuotedText = (text) => {
  const lines = String(text || '').replace(/\r\n/g, '\n').split('\n');
  let cut = lines.findIndex((line) => QUOTE_START.test(line.trim()));
  if (cut === -1) {
    // A trailing run of "> " lines (with blank lines between them).
    let index = lines.length - 1;
    while (index >= 0 && lines[index].trim() === '') index -= 1;
    let start = index + 1;
    while (index >= 0 && (lines[index].trim().startsWith('>') || lines[index].trim() === '')) {
      if (lines[index].trim().startsWith('>')) start = index;
      index -= 1;
    }
    if (start <= lines.length - 1 && start > 0) cut = start;
  }
  if (cut <= 0) return { main: String(text || '').trim(), quoted: '' };
  return { main: lines.slice(0, cut).join('\n').trim(), quoted: lines.slice(cut).join('\n').trim() };
};

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const textToHtml = (text) => escapeHtml(text).replace(/\r?\n/g, '<br>');

// The agent's signature as editor HTML, after two empty lines so the cursor
// can start above it.
export const signatureHtml = (signature) => {
  const lines = String(signature || '').trim().split('\n');
  if (!lines[0]) return '';
  return `<div><br></div><div><br></div><div>--</div>${lines.map((line) => `<div>${escapeHtml(line) || '<br>'}</div>`).join('')}`;
};

// Fills {{customer_name}}, {{order_number}}, {{customer_email}} and
// {{agent_name}}. A value the CRM doesn't have becomes a visible
// "[order number]" for the agent to fill in; nothing is made up.
export const TEMPLATE_VARIABLES = [
  { key: 'customer_name', label: 'customer name' },
  { key: 'order_number', label: 'order number' },
  { key: 'customer_email', label: 'customer email' },
  { key: 'agent_name', label: 'your name' },
];

export const fillTemplate = (text, values = {}) =>
  String(text || '').replace(/\{\{\s*([a-z_]+)\s*\}\}/gi, (whole, key) => {
    const name = key.toLowerCase();
    const value = values[name];
    if (value != null && String(value).trim() !== '') return String(value).trim();
    if (name === 'customer_name') return 'Customer';
    const known = TEMPLATE_VARIABLES.find((item) => item.key === name);
    return known ? `[${known.label}]` : whole;
  });

export const newRequestId = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

// Uploads one file for an outgoing email (POST .../email-attachment) with
// progress. XMLHttpRequest rather than fetch, since fetch can't report
// upload progress. Resolves to the server's {url, filename, size, mimeType}.
export const uploadEmailAttachment = ({ file, conversationKey, token, onProgress }) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `/api/customers/${encodeURIComponent(conversationKey || 'upload')}/email-attachment`);
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100));
    };
    xhr.onload = () => {
      let data = {};
      try {
        data = JSON.parse(xhr.responseText || '{}');
      } catch {
        data = {};
      }
      if (xhr.status === 401) return reject(new Error('Your session has expired. Log in again.'));
      if (xhr.status < 200 || xhr.status >= 300) return reject(new Error(data.error || 'Upload failed'));
      return resolve(data);
    };
    xhr.onerror = () => reject(new Error('Upload failed: check your connection'));
    const form = new FormData();
    form.append('file', file);
    xhr.send(form);
  });

// Turns http(s) links in plain text into anchors; everything else escaped.
export const linkifyText = (text) => {
  const parts = [];
  const pattern = /https?:\/\/[^\s<>"')]+/g;
  let last = 0;
  let match;
  const source = String(text || '');
  while ((match = pattern.exec(source))) {
    if (match.index > last) parts.push({ type: 'text', value: source.slice(last, match.index) });
    parts.push({ type: 'link', value: match[0] });
    last = match.index + match[0].length;
  }
  if (last < source.length) parts.push({ type: 'text', value: source.slice(last) });
  return parts;
};

// Whether an HTML body needs the real HTML view (images, tables, layout)
// rather than the cleaner plain-text one.
export const htmlNeedsRichView = (html) => /<(img|table)\b/i.test(String(html || ''));
